import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { BudgetDisplay } from "@/components/BudgetDisplay";
import { ExpenseInputSheet } from "@/components/Expense/ExpenseInputSheet";
import { AppHeader } from "@/components/Layout/AppHeader";
import { IncomeThisMonth } from "@/components/Income/IncomeThisMonth";
import { MonthSpendingSummary } from "@/components/Budget/MonthSpendingSummary";
import { UpcomingPayments } from "@/components/Recurring/UpcomingPayments";
import { RecentTransactions } from "@/components/Budget/RecentTransactions";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { useUndoStack } from "@/hooks/useUndoStack";
import { useLanguage } from "@/contexts/LanguageContext";
import { PullToRefresh } from "@/components/ui/pull-to-refresh";
import { DashboardCard } from "@/components/ui/dashboard-card";

const BudgetDetail = () => {
  const { t } = useLanguage();
  const [user, setUser] = useState<any>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const navigate = useNavigate();
  const { pushUndo } = useUndoStack(() => setRefreshTrigger(prev => prev + 1));

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (!session) {
        navigate('/auth');
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (!session) {
        navigate('/auth');
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleRefresh = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  const handlePullRefresh = useCallback(async () => {
    setRefreshTrigger(prev => prev + 1);
    // Small delay to show the refresh animation
    await new Promise(resolve => setTimeout(resolve, 500));
  }, []);

  if (!user) {
    return <div className="min-h-screen flex items-center justify-center">{t('common.loading')}</div>;
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader
        showDrawerTrigger={true}
        title={t('nav.budget')}
      />

      <PullToRefresh onRefresh={handlePullRefresh} className="flex-1 pt-20">
        <main className="container mx-auto px-6 pb-24 max-w-2xl space-y-4">
          {/* Budget Display - Primary Focus */}
          <section>
            <BudgetDisplay refreshTrigger={refreshTrigger} userId={user?.id} />
          </section>

          {/* Income This Month - Single collapsible line */}
          {user && (
            <section>
              <IncomeThisMonth userId={user.id} refreshTrigger={refreshTrigger} />
            </section>
          )}

          {/* Upcoming Recurring Payments */}
          {user && (
            <section>
              <UpcomingPayments userId={user.id} refreshTrigger={refreshTrigger} />
            </section>
          )}

          {/* Month Spending Summary */}
          <section>
            <DashboardCard noPadding className="overflow-hidden">
              <div className="p-5">
                <MonthSpendingSummary 
                  userId={user.id}
                  refreshTrigger={refreshTrigger}
                />
              </div>
            </DashboardCard>
          </section>

          {/* Recent Transactions */}
          {user && (
            <section>
              <RecentTransactions userId={user.id} refreshTrigger={refreshTrigger} />
            </section>
          )}
        </main>
      </PullToRefresh>

      {/* Floating Action Button */}
      <Button
        size="icon"
        className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg z-50"
        onClick={() => setShowAddExpense(true)}
      >
        <Plus className="h-6 w-6" />
      </Button>

      {/* Add Expense Sheet */}
      <ExpenseInputSheet
        open={showAddExpense}
        onOpenChange={setShowAddExpense}
        onExpenseAdded={handleRefresh}
        pushUndo={pushUndo}
      />
    </div>
  );
};

export default BudgetDetail;